import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import CreateTripWizard from "@/pages/CreateTripWizard";
import TripDetail from "@/pages/TripDetail";
import ItineraryPrint from "@/pages/ItineraryPrint";
import EditTrip from "@/pages/EditTrip";
import TravelerProfile from "@/pages/TravelerProfile";
import TravelersList from "@/pages/TravelersList";
import BookedTrips from "@/pages/BookedTrips";
import PricingPage from "@/pages/PricingPage";
import SignInPage from "@/pages/SignInPage";
import BillingPage from "@/pages/BillingPage";
import SettingsPage from "@/pages/SettingsPage";
import AuthCallback from "@/components/auth/AuthCallback";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import EventsPage from "@/pages/EventsPage";
import EventDetail from "@/pages/EventDetail";
import EventManifest from "@/pages/EventManifest";
import ClaimTripPage from "@/pages/ClaimTripPage";
import FlightOptionsPrint from "@/pages/FlightOptionsPrint";
import AppLayout from "@/components/layout/AppLayout";
import AiChatWidget from "@/components/ai/AiChatWidget";
import { PulseBeacon } from "@/lib/pulse";

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* Session pulse. Inside the router because it reports client-side
            navigations; renders nothing and emits nothing when logged out. */}
        <PulseBeacon />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/pricing" element={<PricingPage />} />
          {/* /app is the shared shorthand for "open the app". It used to be a
              bare redirect to /dashboard, which bounced unauthenticated
              visitors straight off-origin to the SSO card — a shell with no
              TripMuscle chrome on it (park walk 2026-09-30, episode
              tripmuscle:app-shell:all:judge-flag:empty-shell-with-no-content-areas).
              SignInPage keeps them on our origin with full chrome, and passes
              logged-in visitors through to the dashboard. Earlier history:
              this route never existed and /app rendered nothing but the
              floating chat widget (TripMuscle-1/2, tasks 20a3225d/ee057173). */}
          <Route path="/app" element={<SignInPage />} />
          {/* Print view — protected but chrome-free (renders its own full page). */}
          <Route
            path="/trips/:id/print"
            element={
              <ProtectedRoute>
                <ItineraryPrint />
              </ProtectedRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/trips/new" element={<CreateTripWizard />} />
            <Route path="/trips/:id" element={<TripDetail />} />
            <Route path="/trips/:id/edit" element={<EditTrip />} />
            <Route path="/claim" element={<ClaimTripPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:id" element={<EventDetail />} />
            <Route path="/events/:id/manifest" element={<EventManifest />} />
            {/* § G28 — the printable options sheet for ONE attendee. Behind the
                same gate as the roster it is reached from: it carries fares. */}
            <Route
              path="/events/:groupId/attendees/:membershipId/options/print"
              element={
                <ProtectedRoute>
                  <FlightOptionsPrint />
                </ProtectedRoute>
              }
            />
            <Route path="/travelers" element={<TravelersList />} />
            <Route path="/travelers/:id" element={<TravelerProfile />} />
            <Route path="/bookings" element={<BookedTrips />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
          {/* Catch-all — LAST. Any unmatched path (typo, stale link, dead
              deep-link) lands on the marketing page instead of a blank
              screen with only the chat widget floating on it. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <AiChatWidget />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
